<template>
  <div class="hero-section" dir="rtl">
    <!-- Background gradient -->
    <div class="hero-background"></div>

    <!-- Logo placeholder -->
    <div class="logo-container">
      <a href="http://monglish.co.uk" target="_blank" rel="noopener">
      <img src="/src/assets/monglish.svg" alt="Monglish Logo" class="logo" />
      </a>
    </div>


    <!-- Main content -->
    <div class="hero-content">
      <!-- Top tagline -->
      <p class="tagline">
        {{ t('hero.tagline') }}
      </p>

      <!-- Main headline -->
      <h1 class="main-headline">
        {{ t('hero.headline_part1') }}
        <span class="highlight">{{ t('hero.headline_highlight') }}</span>
        {{ t('hero.headline_part2') }}
      </h1>

      <!-- Subheadline -->
      <p class="subheadline">
        {{ t('hero.subheadline') }}
      </p>

      <!-- CTA Button -->
      <CtaButton
        :title="t('hero.cta_title')"
        :subtitle="t('hero.cta_subtitle')"
      />
    </div>
  </div>
</template>

<script setup>
import CtaButton from '../ui/CtaButton.vue'
import { onMounted } from 'vue'
import { useLanguage } from '@/composables/useLanguage'

const { t, initializeLanguage } = useLanguage()

onMounted(() => {
  initializeLanguage()
})

</script>

<style scoped>
.hero-section {
  width: 100%;
  position: relative;
  overflow: hidden;
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  min-height: 80vh;
}

/* Background gradient */
.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg,
    var(--bg-dark) 0%,
    #001a2e 40%,
    #003d5c 70%,
    var(--color-gradient-start) 100%
  );
  z-index: 0;
}

/* Logo */
.logo-container {
  position: absolute;
  top: var(--spacing-md);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.logo {
  height: clamp(40px, 6vw, 60px);
  width: auto;
  display: block;
}

.logo-placeholder {
  color: var(--bg-light);
  font-weight: 700;
  font-size: clamp(14px, 2vw, 20px);
  font-family: Poppins;
  padding: clamp(8px, 1.5vw, 10px) clamp(12px, 2.5vw, 20px);
  border: 2px solid var(--bg-light);
  border-radius: 8px;
}


/* Main content */
.hero-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1400px;
  text-align: center;
  margin-top: clamp(80px, 12vw, 120px);
  padding: 0 var(--spacing-md);
  color: var(--bg-light);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
}

/* RTL specific - Reduced spacing */
html[lang="ar"] .hero-content {
  margin-top: clamp(60px, 8vw, 80px);
}

.tagline {
  font-size: clamp(18px, 3.5vw, 24px);
  line-height: 2.2;
  margin-bottom: clamp(16px, 3vw, 24px);
  opacity: 0.9;
  font-weight: 400;
  width: 100%;
  max-width: 100%;
}

/* RTL specific - Reduced margin */
html[lang="ar"] .tagline {
  margin-bottom: clamp(8px, 1.5vw, 12px);
  line-height: 2.1;
}

.main-headline {
  font-size: clamp(26px, 5vw, 38px);
  font-weight: 800;
  line-height: 1.8;
  margin-bottom: clamp(16px, 3vw, 24px);
  text-transform: uppercase;
  letter-spacing: -0.02em;
  padding: 0 clamp(10px, 2vw, 20px);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* RTL specific - Reduced margin and padding */
html[lang="ar"] .main-headline {
  margin-bottom: clamp(8px, 1.5vw, 12px);
  padding: 0 clamp(5px, 1vw, 10px);
  line-height: 1.75;
}

.main-headline .highlight {
  color: var(--color-primary);
  display: inline-block;
}

.subheadline {
  font-size: clamp(16px, 3vw, 22px);
  line-height: 2.3;
  margin-bottom: clamp(24px, 4vw, 32px);
  opacity: 0.85;
  width: 100%;
  max-width: 100%;
}

/* RTL specific - Reduced margin */
html[lang="ar"] .subheadline {
  margin-bottom: clamp(12px, 2vw, 16px);
  line-height: 2.2;
}

/* CTA Button */
:deep(.cta-button) {
  margin: 0 auto clamp(40px, 6vw, 60px);
}

/* RTL specific - Reduced margin */
html[lang="ar"] :deep(.cta-button) {
  margin: 0 auto clamp(20px, 3vw, 30px);
}

/* Video Container */
.video-container {
  position: relative;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 clamp(15px, 3vw, 30px);
  z-index: 1;
}

.ipad-mockup {
  position: relative;
  width: 100%;
  max-width: 850px;
  margin: 0 auto;
  padding: clamp(10px, 2vw, 16px);
  background: var(--bg-dark);
  border-radius: clamp(16px, 3vw, 24px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.3);
}

.ipad-screen {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border-radius: clamp(12px, 2vw, 16px);
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
}

.ipad-screen:hover {
  opacity: 0.9;
}

.play-button {
  width: clamp(50px, 8vw, 80px);
  height: clamp(35px, 5.6vw, 56px);
  transition: transform 0.3s ease;
}

.ipad-screen:hover .play-button {
  transform: scale(1.1);
}

/* Floating info cards */
.info-card {
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: clamp(12px, 2vw, 20px);
  padding: clamp(14px, 2.5vw, 20px) clamp(16px, 3vw, 24px);
  color: var(--bg-light);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.left-card {
  bottom: clamp(20px, 10vw, 80px);
  left: clamp(-180px, -20vw, -300px);
  max-width: clamp(240px, 40vw, 320px);
}

.card-text {
  font-size: 18px;
  line-height: 1.4;
  font-weight: 500;
}

.right-card {
  top: 50%;
  right: clamp(-200px, -23vw, -350px);
  transform: translateY(-50%);
  background: rgba(22, 94, 132, 0.4);
  padding: clamp(16px, 3vw, 24px) clamp(18px, 3.5vw, 28px);
}

.stats-grid {
  display: flex;
  gap: clamp(12px, 3vw, 24px);
  align-items: center;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label-small {
  font-size: 14px;
  opacity: 0.9;
}

.stat-label {
  font-size: 16px;
  font-weight: 500;
  opacity: 0.95;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-primary);
  margin-top: 4px;
}

.highlight-value {
  color: var(--color-primary);
}

.stat-divider {
  width: 1px;
  height: clamp(40px, 8vw, 60px);
  background: rgba(255, 255, 255, 0.3);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .left-card {
    left: 0;
    bottom: -60px;
    max-width: 100%;
    position: relative;
  }

  .right-card {
    position: relative;
    transform: none;
    margin: 20px auto 0;
    max-width: 100%;
    top: auto;
    right: auto;
  }

  .video-container {
    margin-bottom: clamp(100px, 15vw, 140px);
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: clamp(12px, 2.5vw, 15px);
  }

  .hero-content {
    margin-top: clamp(60px, 9vw, 70px);
    padding: 0 clamp(12px, 2.5vw, 15px);
  }

  .stats-grid {
    flex-direction: column;
    gap: clamp(12px, 3vw, 16px);
    align-items: flex-start;
  }

  .stat-divider {
    width: 100%;
    height: 1px;
  }

  .left-card,
  .right-card {
    position: relative;
    margin: 16px auto;
    left: 0;
    right: 0;
    bottom: auto;
    top: auto;
    transform: none;
    max-width: 100%;
  }

  .video-container {
    margin-bottom: 80px;
    padding: 0 10px;
  }

  .main-headline {
    line-height: 1.2;
  }

  .card-text {
    font-size: 16px;
  }

  .stat-label {
    font-size: 14px;
  }

  .stat-value {
    font-size: 16px;
  }
}

@media (max-width: 640px) {
  .hero-content {
    margin-top: clamp(50px, 8vw, 60px);
    padding: 0 clamp(10px, 2vw, 15px);
  }

  .main-headline {
    line-height: 1.15;
    letter-spacing: -0.01em;
  }

  :deep(.cta-button) {
    margin-bottom: 30px;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding: 12px;
  }

  .hero-content {
    margin-top: 50px;
    padding: 0 10px;
  }

  .left-card,
  .right-card {
    margin: 12px 0;
  }

  .video-container {
    margin-bottom: 60px;
    padding: 0 5px;
  }

  .card-text {
    font-size: 14px;
  }

  .stat-label-small {
    font-size: 12px;
  }

  .stat-label {
    font-size: 12px;
  }

  .stat-value {
    font-size: 14px;
  }
}

@media (max-width: 380px) {
  .hero-section {
    padding: 10px;
  }

  .hero-content {
    margin-top: 45px;
    padding: 0 8px;
  }
}

</style>
